import { FlakinessReport as FK, FlakinessReport } from '@flakiness/flakiness-report';
import { Multimap } from '@flakiness/shared/common/multimap.js';
import { Brand, xxHashObject } from '@flakiness/shared/common/utils.js';
import { SyncComputationCache } from '../computationCache.js';
import { Ranges } from '../ranges.js';
import { WireTypes } from '../wireTypes.js';
import { EnvGroup } from './envGroup.js';
import { Histogram as H } from './histogram.js';
import { ResolvedTimeline } from './resolvedTimeline.js';
import { Stats as S, Stats } from './stats.js';
import { StatsAnnotationProfile } from './statsAnalyzer.js';
import { TestOutcomes as T } from './testOutcomes.js';

const EMPTY_LINE_NUMBERS = new Int32Array(0);

export type TimelineCounters = {
  outcomes: T.TestOutcomes,
  errors: {
    error: WireTypes.TestError,
    impactedTimelines: number,
    impactedTests: number,
  }[],
  annotations: {
    annotation: WireTypes.Annotation,
    annotatedTests: number,
    annotatedTimelines: number,
  }[],
  tags: {
    tag: string,
    impactedTimelines: number,
    impactedTests: number,
  }[],
}

export type RunReport = {
  runId: S.RunId,
  duration: FK.DurationMS,
  startTimestamp: FK.UnixTimestampMS,
  url: string,
  title?: string,
  outcomes: T.TestOutcomes,
  environments: S.EnvIndex[],
  testsWithImages: T.TestRanges,
  testsWithTraces: T.TestRanges,
  testsWithVideos: T.TestRanges,
}

export type Contribution = {
  outcomes: T.TestOutcomes,
  jsonRun: S.JSONRun,
  jsonAttempt: S.JSONAttempt,
  commitAnalyzer: CommitAnalyzer,
}

export class CommitAnalyzer {
  static regressify(contribs: ContributionReport, unhealthyTests: T.TestRanges): ContributionReport {
    return contribs.map(contrib => {
      const contribOutcomes = T.regressify(contrib.outcomes, unhealthyTests);
      return {
        commitAnalyzer: contrib.commitAnalyzer,
        jsonAttempt: contrib.jsonAttempt,
        jsonRun: contrib.jsonRun,
        outcomes: contribOutcomes,
      } satisfies Contribution;
    }).filter(c => !T.isEmptyOutcomes(c.outcomes)) as ContributionReport;
  }

  static computeDurationBuckets(timeline: ResolvedTimeline, contribs: ContributionReport): H.Histogram<FK.DurationMS, Stats.TestIndex>[] {
    const histograms: H.Histogram<FK.DurationMS, S.TestIndex>[] = [];
    for (const contrib of contribs) {
      const contribTests = T.flatten(contrib.outcomes);
      if (!contribTests.length)
        continue;
      for (const jsonAttempt of contrib.jsonRun.attempts) {
        if (!timeline.acceptsEnvironment(jsonAttempt.environmentIdx))
          continue;

        const h: H.Histogram<FK.DurationMS, S.TestIndex> = jsonAttempt.durationBuckets.map((duration, bucketIdx) => [duration, Ranges.decompress(jsonAttempt.durationBucketTests[bucketIdx])]);
        histograms.push(H.intersect(h, contribTests));
      }
    }
    return histograms.filter(h => !!h.length);
  }

  static computeErrorTests(contribs: ContributionReport): Map<WireTypes.TestError, T.TestRanges> {
    const errors = new Map<S.ErrorId, WireTypes.TestError>();
    const errorTests = new Multimap<S.ErrorId, T.TestRanges>();
    for (const contrib of contribs) {
      // TODO: we seem to be computing this over and over again.
      const contribTests = T.flatten(contrib.outcomes);
      if (!contribTests.length)
        continue;
      const fc = contrib.commitAnalyzer;
      for (const { errorIdx, tests } of contrib.jsonAttempt.errors ?? []) {
        const error = fc._errs[errorIdx];
        const errTests = Ranges.intersect(contribTests, Ranges.decompress(tests));
        if (errTests.length) {
          errors.set(error.errorId, error);
          errorTests.set(error.errorId, errTests);
        }
      }
    }
    return new Map(errorTests.map((errorId, manyTests) => [errors.get(errorId)!, Ranges.unionAll(manyTests)]));
  }

  static computeAnnotationTests(contribs: ContributionReport): Map<WireTypes.Annotation, T.TestRanges> {
    const annotations = new Map<S.AnnotationId, WireTypes.Annotation>();
    const annotationTests = new Multimap<S.AnnotationId, T.TestRanges>();
    for (const contrib of contribs) {
      const contribTests = T.flatten(contrib.outcomes);
      if (!contribTests.length)
        continue;
      const fc = contrib.commitAnalyzer;
      for (const { annotationIdx, tests } of fc._annotationProfiles[contrib.jsonAttempt.annotationsProfileIdx] ?? []) {
        const annotation = fc._annotations[annotationIdx];
        const t = Ranges.intersect(contribTests, tests);
        if (t.length) {
          annotations.set(annotation.annotationId, annotation);
          annotationTests.set(annotation.annotationId, t);
        }
      }
    }
    return new Map(annotationTests.map((annotationId, manyTests) => [annotations.get(annotationId)!, Ranges.unionAll(manyTests)]));
  }

  static computeTagTests(contribs: ContributionReport): Map<string, T.TestRanges> {
    const tags = new Multimap<string, T.TestRanges>();
    for (const contrib of contribs) {
      const contribTests = T.flatten(contrib.outcomes);
      if (!contribTests.length)
        continue;

      const fc = contrib.commitAnalyzer;
      for (const { tag, tests } of contrib.commitAnalyzer._tags) {
        const tagTests = Ranges.intersect(contribTests, tests);
        if (tagTests.length)
          tags.set(tag, tagTests);
      }
    }
    return new Map(tags.map((tag, manyTests) => [tag, Ranges.unionAll(manyTests)]));
  }

  static computeLineNumbers(contribs: ContributionReport): Int32Array {
    const maxTestIdx = Math.max(0, ...contribs.map(contrib => contrib.commitAnalyzer.lineNumbers.length));
    const lineNumbers = new Int32Array(maxTestIdx);
    for (const contrib of contribs) {
      const contribTests = T.flatten(contrib.outcomes);
      if (!contribTests.length)
        continue;

      const fc = contrib.commitAnalyzer;
      for (const testIdx of Ranges.iterate(contribTests))
        lineNumbers[testIdx] = fc.lineNumbers[testIdx];
    }
    return lineNumbers;
  }

  private _runIdToJSONRun = new Map<S.RunId, S.JSONRun>();

  private _runRawContributions = new Map<S.RunId, Contribution[]>();

  private _runContributionReport = new SyncComputationCache<{
    runId: S.RunId,
    timeline: ResolvedTimeline,
  }, ContributionReport>({
    size: EnvGroup.MAX_TIMELINES,
    etag: ({ runId, timeline }) => xxHashObject({ runId, timeline: timeline.etag() }),
    compute: ({ runId, timeline }) => {
      const allContributions = (this._runRawContributions.get(runId) ?? [])
          .filter(c => timeline.acceptsEnvironment(c.jsonAttempt.environmentIdx));
      return createRunContributionReport(allContributions.toReversed());
    }
  });

  private _commitContributionReport = new SyncComputationCache<ResolvedTimeline, ContributionReport>({
    size: EnvGroup.MAX_TIMELINES,
    etag: timeline => timeline.etag(),
    compute: timeline => {
      const contribs = [];
      for (const contributions of this._runRawContributions.values()) {
        for (const c of contributions) {
          if (timeline.acceptsEnvironment(c.jsonAttempt.environmentIdx))
            contribs.push(c);
        }
      }
      return createRunContributionReport(contribs.toReversed());
    }
  });

  private _testOutcomes = new SyncComputationCache<ResolvedTimeline, T.TestOutcomes>({
    size: EnvGroup.MAX_TIMELINES,
    etag: timeline => timeline.etag(),
    compute: timeline => {
      return T.unionAll(this._commitContributionReport.get(timeline).map(c => c.outcomes));
    }
  });

  private _durations = new SyncComputationCache<ResolvedTimeline, H.Histogram<FK.DurationMS, Stats.TestIndex>[]>({
    size: EnvGroup.MAX_TIMELINES,
    etag: timeline => timeline.etag(),
    compute: timeline => {
      const contribs = this._commitContributionReport.get(timeline);
      return CommitAnalyzer.computeDurationBuckets(timeline, contribs);
    }
  });

  private _runReports = new SyncComputationCache<ResolvedTimeline, RunReport[]>({
    size: EnvGroup.MAX_TIMELINES,
    etag: timeline => timeline.etag(),
    compute: timeline => {
      const runReports: RunReport[] = [];
      for (const [runId, jsonRun] of this._runIdToJSONRun) {
        let testsWithImages: T.TestRanges[] = [];
        let testsWithTraces: T.TestRanges[] = [];
        let testsWithVideos: T.TestRanges[] = [];
        const outcomes = T.newOutcomes();
        const contribs = this._runContributionReport.get({ runId, timeline: timeline });
        const envs = new Set<S.EnvIndex>();
        for (let contrib of contribs) {
          const contribTests = T.flatten(contrib.outcomes);
          if (!contribTests.length)
            continue;
          envs.add(contrib.jsonAttempt.environmentIdx);
          T.unionInplace(outcomes, contrib.outcomes);
          if (contrib.jsonAttempt.testsWithImage)
            testsWithImages.push(Ranges.intersect(contribTests, Ranges.decompress(contrib.jsonAttempt.testsWithImage)));
          if (contrib.jsonAttempt.testsWithVideo)
            testsWithVideos.push(Ranges.intersect(contribTests, Ranges.decompress(contrib.jsonAttempt.testsWithVideo)));
          if (contrib.jsonAttempt.testsWithTrace)
            testsWithTraces.push(Ranges.intersect(contribTests, Ranges.decompress(contrib.jsonAttempt.testsWithTrace)));
        }
        if (T.isEmptyOutcomes(outcomes))
          continue;
        runReports.push({
          runId: runId,
          environments: Array.from(envs),
          duration: jsonRun.duration,
          startTimestamp: jsonRun.startTimestamp,
          url: jsonRun.url,
          title: jsonRun.title,
          outcomes: outcomes,
          testsWithImages: Ranges.unionAll(testsWithImages),
          testsWithTraces: Ranges.unionAll(testsWithTraces),
          testsWithVideos: Ranges.unionAll(testsWithVideos),
        });
      }
      return runReports;
    }
  });

  private _tags: { tag: string, tests: T.TestRanges }[] = [];
  public readonly lineNumbers: Int32Array;

  private _etag: string;

  static createEmpty(commitId: S.CommitId) {
    return new CommitAnalyzer(commitId, '<empty>' as S.TestsEpoch, [], [], [], EMPTY_LINE_NUMBERS, c => Ranges.decompress(c));
  }

  static computeEtag(epoch: S.TestsEpoch, commitId: S.CommitId, json?: S.JSONCommit) {
    return xxHashObject({
      commitId,
      epoch,
      version: S.STATS_VERSION,
      runs: (json?.runs ?? []).map(run => run.runId),
    });
  }

  
  constructor(
    public readonly commitId: S.CommitId,
    private _epoch: S.TestsEpoch,
    private _errs: WireTypes.TestError[],
    private _annotationProfiles: StatsAnnotationProfile[],
    private _annotations: WireTypes.Annotation[],
    lineNumbers: Int32Array,
    // Used by the constructor only. The analyzer does not keep it, so that it
    // holds no reference to the `StatsAnalyzer` that created it.
    decompress: (c: Ranges.CompressedRanges<S.TestIndex>) => T.TestRanges,
    private _jsonCommit?: S.JSONCommit,
    runIds?: Set<S.RunId>,
  ) {
    this._etag = CommitAnalyzer.computeEtag(this._epoch, this.commitId, this._jsonCommit);

    for (const run of this._jsonCommit?.runs ?? []) {
      // Filter out runs that we do not want to take into account.
      if (runIds && !runIds.has(run.runId))
        continue;
      const contributions: Contribution[] = run.attempts.map(attempt => ({
        commitAnalyzer: this,
        outcomes: {
          regressed: Ranges.EMPTY as T.TestRanges,
          unexpected: attempt.unexpectedTests ? decompress(attempt.unexpectedTests) : T.EMPTY_TESTS,
          expected: attempt.expectedTests ? decompress(attempt.expectedTests) : T.EMPTY_TESTS,
          skipped: attempt.skippedTests ? decompress(attempt.skippedTests) : T.EMPTY_TESTS,
          flaked: attempt.flakedTests ? decompress(attempt.flakedTests) : T.EMPTY_TESTS,
        },
        jsonRun: run,
        jsonAttempt: attempt,
      }));
      this._runIdToJSONRun.set(run.runId, run);
      this._runRawContributions.set(run.runId, contributions);
    }

    this._tags = (this._jsonCommit?.tags ?? []).map(({ tag, tests }) => ({
      tag,
      tests: decompress(tests),
    }));

    // Copying is O(tests in the epoch) and dominates construction on projects with
    // many tests, so only commits that actually move line numbers get their own copy.
    // Everyone else shares the shard-wide array, which is never mutated.
    const changedLineNumbers = this._jsonCommit?.changedLineNumbers ?? [];
    if (!changedLineNumbers.length) {
      this.lineNumbers = lineNumbers;
    } else {
      const adjusted = new Int32Array(lineNumbers);
      for (const { lineDelta, affectedTests } of changedLineNumbers) {
        for (const testIdx of Ranges.iterate(decompress(affectedTests)))
          adjusted[testIdx] += lineDelta;
      }
      this.lineNumbers = adjusted;
    }
  }

  /**
   * Returns this analyzer if `jsonCommit` has the runs it was built from, in the
   * same order. A run never changes once it is in a shard, so the same runs mean
   * the same commit. The order counts because a test's outcome is attributed to
   * the newest run that produced it.
   */
  tryToReuse(jsonCommit: S.JSONCommit): CommitAnalyzer|undefined {
    const runs = this._jsonCommit?.runs ?? [];
    if (runs.length !== jsonCommit.runs.length)
      return undefined;
    return runs.every((run, idx) => run.runId === jsonCommit.runs[idx].runId) ? this : undefined;
  }

  isEmpty() {
    return this._runIdToJSONRun.size === 0;
  }

  etag() { 
    return this._etag;
  }

  contributions(timeline: ResolvedTimeline): ContributionReport {
    return this._commitContributionReport.get(timeline);
  }

  testOutcomes(timeline: ResolvedTimeline): T.TestOutcomes {
    return this._testOutcomes.get(timeline);
  }

  runReports(timeline: ResolvedTimeline): RunReport[] {
    return this._runReports.get(timeline);
  }

  durations(timeline: ResolvedTimeline): H.Histogram<FlakinessReport.DurationMS, Stats.TestIndex>[] {
    return this._durations.get(timeline);
  }

  contributionInfo(contrib: Contribution) {
    return {
      testsWithImage: contrib.jsonAttempt.testsWithImage ? Ranges.decompress(contrib.jsonAttempt.testsWithImage) : T.EMPTY_TESTS,
      testsWithTrace: contrib.jsonAttempt.testsWithTrace ? Ranges.decompress(contrib.jsonAttempt.testsWithTrace) : T.EMPTY_TESTS,
      testsWithVideo: contrib.jsonAttempt.testsWithVideo ? Ranges.decompress(contrib.jsonAttempt.testsWithVideo) : T.EMPTY_TESTS,
    };
  }
}

export type ContributionReport = Brand<Contribution[], 'ContributionReport'>;

export function createCommitContributionReport(contribs: Contribution[]): ContributionReport {
  let seenTests = T.EMPTY_TESTS;
  const result: Contribution[] = [];
  for (const contrib of contribs) {
    const cTests = T.flatten(contrib.outcomes);
    const unseenTests = Ranges.subtract(cTests, seenTests);
    if (!unseenTests.length)
      continue;
    seenTests = Ranges.union(unseenTests, seenTests);
    const cOutcomes = T.intersectRanges(contrib.outcomes, unseenTests);
    result.push({
      commitAnalyzer: contrib.commitAnalyzer,
      jsonRun: contrib.jsonRun,
      jsonAttempt: contrib.jsonAttempt,
      outcomes: cOutcomes
    });
  }
  return result as ContributionReport;
}

/**
 * Every test gets the worst outcome it had anywhere in the span, attributed to the newest
 * contribution that produced that outcome. Unlike `createCommitContributionReport`, a test that
 * failed once and passed later stays failed.
 */
export function createWorstContributionReport(contribs: Contribution[]): ContributionReport {
  const worstOutcomes = T.newOutcomes();
  for (const contrib of contribs)
    T.unionInplace(worstOutcomes, contrib.outcomes);
  // A test that lands in several outcomes keeps the worst of them.
  T.normalizeInplace(worstOutcomes);

  const result: Contribution[] = [];
  let leftoverOutcomes = worstOutcomes;
  for (const contrib of contribs) {
    if (T.isEmptyOutcomes(leftoverOutcomes))
      break;
    const intersection = T.intersectWithOutcomes(leftoverOutcomes, contrib.outcomes);
    if (T.isEmptyOutcomes(intersection))
      continue;
    leftoverOutcomes = T.subtractOutcomes(leftoverOutcomes, intersection);
    result.push({ ...contrib, outcomes: intersection });
  }
  return result as ContributionReport;
}

export function createRunContributionReport(contribs: Contribution[]): ContributionReport {
  const finalOutcomes = T.newOutcomes();
  for (const contrib of contribs)
    T.unionInplace(finalOutcomes, contrib.outcomes);
  T.addOverlapAsFlakyInplace(finalOutcomes);
  T.normalizeInplace(finalOutcomes);

  const result: Contribution[] = [];
  let leftoverOutcomes = finalOutcomes;
  for (let idx = 0; !T.isEmptyOutcomes(leftoverOutcomes) && idx < contribs.length; ++idx) {
    const contrib = contribs[idx];
    const outcomes = T.cloneOutcomes(contrib.outcomes);
    // Either flaked or unexpected test outcomes could've contributed to final "flaked" status.
    outcomes.flaked = Ranges.union(outcomes.flaked, outcomes.unexpected);
    const intersection = T.intersectWithOutcomes(leftoverOutcomes, outcomes);
    if (T.isEmptyOutcomes(intersection))
      continue;
    leftoverOutcomes = T.subtractOutcomes(leftoverOutcomes, intersection);
    result.push({ ...contrib, outcomes: intersection });
  }
  return result as ContributionReport;
}
